import React, { useMemo } from 'react';

type Row = Record<string, any>;

function toMonthKey(d: any): string {
  // Numeric month index -> label like m0, m1, ...
  if (typeof d === 'number') {
    return `m${d}`;
  }
  // Numeric-like string
  if (typeof d === 'string' && /^m?\d+$/.test(d)) {
    return d.startsWith('m') ? d : `m${d}`;
  }
  // Date-like string or Date
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (dt && typeof dt.getFullYear === 'function') {
    const y = dt.getFullYear();
    const m = (dt.getMonth() + 1).toString().padStart(2, '0');
    return `${y}-${m}`;
  }
  // Fallback to string
  return String(d ?? '');
}

export const MonthlyPivotTable: React.FC<{
  rows: Row[];
  idField: string;
  dateField: string;
  valueField: string;
  maxMonths?: number;
  idHeader?: string;
  valueFormatter?: (v: number | null) => React.ReactNode;
  actionHeader?: string;
  onAddRow?: (id: string) => void;
  baseMonth?: string; // ISO YYYY-MM of m0
  onRowClick?: (id: string) => void;
  renderIdCell?: (id: string) => React.ReactNode;
}> = ({ rows, idField, dateField, valueField, maxMonths = 12, idHeader = 'Article', valueFormatter, actionHeader = 'Actions', onAddRow, baseMonth, onRowClick, renderIdCell }) => {
  const { months, pivotRows } = useMemo(() => {
    if (!rows || rows.length === 0) return { months: [] as string[], pivotRows: [] as Row[] };

    // Collect month keys
    const monthSet = new Set<string>();
    for (const r of rows) {
      const d = r[dateField];
      if (d === undefined || d === null) continue;
      try {
        const mk = toMonthKey(d);
        if (mk !== '') monthSet.add(mk);
      } catch {}
    }
    const allMonths = Array.from(monthSet);
    const numericKeys = allMonths.length > 0 && allMonths.every((k) => /^m?\d+$/.test(String(k)));
    const sortedMonths = allMonths.sort((a, b) => {
      if (numericKeys) {
        const pa = parseInt(String(a).replace(/^m/, ''), 10);
        const pb = parseInt(String(b).replace(/^m/, ''), 10);
        return pa - pb;
      }
      return String(a).localeCompare(String(b));
    });
    const months = sortedMonths.slice(Math.max(0, sortedMonths.length - maxMonths));

    // Group by id and fill values
    const byId: Record<string, Record<string, number | null>> = {};
    for (const r of rows) {
      const id = String(r[idField]);
      const d = r[dateField];
      const raw = (r as any)[valueField];
      if (!id || d === undefined || d === null) continue;
      const mk = toMonthKey(d);
      if (!months.includes(mk)) continue;
      byId[id] = byId[id] || {};
      const current = byId[id][mk];
      const nextVal = (raw === null || raw === undefined) ? null : (isFinite(Number(raw)) ? Number(raw) : 0);
      // If multiple rows per cell, prefer numeric sum; null wins only if both are null
      if (current === undefined) byId[id][mk] = nextVal;
      else if (current === null && nextVal === null) byId[id][mk] = null;
      else byId[id][mk] = Number(current || 0) + Number(nextVal || 0);
    }

    const pivotRows = Object.keys(byId).sort().map((id) => {
      const base: Row = { _id: id };
      for (const m of months) base[m] = (byId[id][m] !== undefined ? byId[id][m] : null);
      return base;
    });

    return { months, pivotRows };
  }, [rows, idField, dateField, valueField, maxMonths]);

  // Render labels: if keys are numeric (mX), map to calendar using baseMonth
  const monthLabels = useMemo(() => {
    if (!months || months.length === 0) return [] as string[];
    const allNumeric = months.every((k) => /^m?\d+$/.test(String(k)));
    if (!allNumeric || !baseMonth) return months as string[];
    const [by, bm] = baseMonth.split('-').map((x) => parseInt(x, 10));
    const labels: string[] = [];
    for (const k of months as string[]) {
      const idx = parseInt(k.replace(/^m/, ''), 10);
      const date = new Date(by, bm - 1 + idx, 1);
      const y = date.getFullYear();
      const m = (date.getMonth() + 1).toString().padStart(2, '0');
      labels.push(`${y}-${m}`);
    }
    return labels;
  }, [months, baseMonth]);

  return (
    <div className="overflow-x-auto overflow-y-auto border rounded">
      <table className="w-max text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-gray-50 text-gray-700">
            <th className="px-3 py-2 text-left whitespace-nowrap bg-gray-50">{idHeader}</th>
            {months.map((m, i) => (
              <th key={String(m)} className="px-3 py-2 text-right whitespace-nowrap bg-gray-50">{monthLabels[i] ?? String(m)}</th>
            ))}
            {onAddRow && (
              <th className="px-3 py-2 text-center whitespace-nowrap bg-gray-50">{actionHeader}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {pivotRows.map((r) => (
            <tr key={r._id} className="border-t">
              <td className="px-3 py-2 cursor-pointer" onClick={() => onRowClick && onRowClick(String(r._id))}>
                {renderIdCell ? renderIdCell(String(r._id)) : r._id}
              </td>
              {months.map((m) => (
                <td key={m} className="px-3 py-2 text-right">
                  {valueFormatter
                    ? valueFormatter((r[m] as number | null | undefined) ?? null)
                    : ((r[m] === null || r[m] === undefined) ? '—' : Number(r[m] as number).toString())}
                </td>
              ))}
              {onAddRow && (
                <td className="px-3 py-2 text-center">
                  <button
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
                    onClick={() => onAddRow(String(r._id))}
                  >
                    Ajouter
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MonthlyPivotTable;


